import React, { useState } from "react";

// components imports
import AllMonthlyInvoicesTable from "../../../../../Components/BoltTable/BoltTable";
import Tooltip from "../../../../../Components/Tooltip/Tooltip";
import CircularLoader from "../../../../../Components/CircularLoader/CircularLoader";

// helpers imports
import { convertTime } from "../../../../../helpers/helpers";

// apis imports
import { fetchAllMonthlyInvoices } from "../../../../../services/AdminServices/PaymentsAdmin/payments.services";

// library imports
import { MdOutlineStickyNote2 } from "react-icons/md";
import { useTranslation } from "react-i18next";

const MonthlyInvoices = ({ setSnack }) => {
  const { t } = useTranslation("admin");
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoiceId, setLoadingInvoiceId] = useState(null);

  const defaultWidth = "200px";

  const getMonthlyInvoices = async (page, perPage, filter) => {
    const response = await fetchAllMonthlyInvoices(page, perPage, filter);

    if (!response.error) {
      setInvoices(response?.data?.result);
    } else {
      setSnack((snack) => ({
        ...snack,
        open: true,
        text: t("common:messages.somethingWentWrong"),
        severity: "error",
      }));
    }
  };

  const downloadPdf = async (data) => {
    if (!data?.file) return;

    setLoadingInvoiceId(data.id);

    try {
      const urlfull = data.file;
      const filename = urlfull.split("/").pop();

      const response = await fetch(urlfull, {
        method: "GET",
        headers: {
          "Content-Type": "application/pdf",
        },
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      setSnack((snack) => ({
        ...snack,
        open: true,
        text: t("payments.failedToDownloadInvoice"),
        severity: "error",
      }));
    } finally {
      setLoadingInvoiceId(null);
    }
  };

  const handleUserRole = (row) => {
    const role = row?.role || "-";

    return role === "warehouseUser"
      ? t("payments.roleWarehouseUser")
      : t(`users.roles.${role}`, { defaultValue: role });
  };

  const columns = [
    {
      id: 0,
      label: "#",
      render: (row) => <>{row?.id ?? "-"}</>,
      width: defaultWidth,
    },
    {
      id: 1,
      label: t("payments.clientName"),
      render: (row) => <>{row?.user?.username ?? "-"}</>,
      width: defaultWidth,
    },
    {
      id: 2,
      label: t("payments.clientRole"),
      render: (row) => <>{handleUserRole(row?.user)}</>,
      width: defaultWidth,
    },
    {
      id: 3,
      label: t("payments.invoiceNumber"),
      render: (row) => <>{row?.invoice_no ?? "-"}</>,
      width: defaultWidth,
    },
    {
      id: 4,
      label: t("payments.subTotal"),
      render: (row) => <>{row?.actual_price ?? "-"}</>,
      width: defaultWidth,
    },
    {
      id: 5,
      label: t("payments.taxes"),
      render: (row) =>
        row?.tax_details?.length
          ? row.tax_details.map((tax) => tax.name).join(", ")
          : "-",
      width: defaultWidth,
    },
    {
      id: 6,
      label: t("payments.taxTotal"),
      render: (row) => <>{row?.tax_price ?? "-"}</>,
      width: defaultWidth,
    },
    {
      id: 7,
      label: t("payments.total"),
      render: (row) => <>{row?.total_price ?? "-"}</>,
      width: defaultWidth,
    },
    {
      id: 8,
      label: t("payments.createdAt"),
      render: (row) => <>{convertTime(row?.created_at)}</>,
      width: defaultWidth,
    },
    {
      id: 9,
      label: t("payments.actions"),
      render: (row) => (
        <div className="table_action__wrap">
          <Tooltip title={t("payments.downloadInvoice")}>
            <div className="table_action__btn" onClick={() => downloadPdf(row)}>
              {loadingInvoiceId === row.id ? (
                <CircularLoader size={10} />
              ) : (
                <MdOutlineStickyNote2 size={10} />
              )}
              {t("payments.invoiceButton")}
            </div>
          </Tooltip>
        </div>
      ),
      width: defaultWidth,
      fixed: "right",
    },
  ];

  return (
    <div>
      <AllMonthlyInvoicesTable
        tableTitle={t("payments.invoices")}
        tableId="AllMonthlyInvoicesTableForAdmin"
        message={t("payments.noInvoicesAvailableToDisplay")}
        searchPlaceHolder={t("payments.searchNotAvailable")}
        columns={columns}
        data={invoices}
        getAllData={getMonthlyInvoices}
      />
    </div>
  );
};

export default MonthlyInvoices;
